import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { useOpenCvTools } from '@hooks/useOpenCvTools';
import { useSettings } from '@hooks/useSettings';
import { Coordinates, CropPointId, CropPointsCoordinates, points } from '@rdTypes/shared';
import CrossOriginImg from '@uiKit/CrossOriginImg';

import { CropEdges } from './components/CropEdges';
import { CropMagnifier } from './components/CropMagnifier';
import { CropPoint } from './components/CropPoint';

export type ContainerSize = {
  width: number;
  height: number;
};

export interface ICropperRef {
  done: () => Promise<any>;
}

const transformCornerPoints = (
  coordinates: CropPointsCoordinates,
  transformer: any,
  xProp: string = 'width',
  yProp: string = 'height',
) =>
  points.reduce<CropPointsCoordinates>((acc, item: CropPointId) => {
    acc[item] = {
      x: transformer[xProp] * coordinates[item].x,
      y: transformer[yProp] * coordinates[item].y,
    };
    return acc;
  }, {} as CropPointsCoordinates);
const defaultCropPointsCoordinates: CropPointsCoordinates = {
  topLeft: { x: 0, y: 0 },
  topRight: { x: 1, y: 0 },
  bottomLeft: { x: 0, y: 1 },
  bottomRight: { x: 1, y: 1 },
};

const settingName = ['ComputerVision'];

interface CanvasProps {
  image?: string;
  cropperRef: React.MutableRefObject<ICropperRef | null> | undefined;
  setOpenCvIsLoading?: (status: boolean) => void;
}

const isConvexQuadrilateral = (updatedPoints: CropPointsCoordinates): boolean => {
  const crossProduct = (o: Coordinates, a: Coordinates, b: Coordinates): number => {
    return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  };

  const orderedPoints = [
    updatedPoints.topLeft,
    updatedPoints.topRight,
    updatedPoints.bottomRight,
    updatedPoints.bottomLeft,
  ];

  let sign: boolean | null = null;

  for (let i = 0; i < 4; i++) {
    const cp = crossProduct(orderedPoints[i], orderedPoints[(i + 1) % 4], orderedPoints[(i + 2) % 4]);

    if (cp !== 0) {
      const currentSign = cp > 0;
      if (sign === null) {
        sign = currentSign;
      } else if (currentSign !== sign) {
        return false;
      }
    }
  }

  return true;
};

export const ImageCropper = ({ image, cropperRef, setOpenCvIsLoading }: CanvasProps) => {
  const cropContainerRef = useRef<HTMLDivElement>(null);

  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  const [cropContainerSize, setCropContainerSize] = useState<ContainerSize>({ width: 0, height: 0 });
  const [detectedCornersCoordinates, setDetectedCornersCoordinates] = useState<CropPointsCoordinates | null>(null);
  const [relativeCropPointsCoordinates, setRelativeCropPointCoordinates] =
    useState<CropPointsCoordinates>(defaultCropPointsCoordinates);
  const [magnifierPoint, setMagnifierPoint] = useState<CropPointId | null>(null);
  const [aspectRatio, setAspectRatio] = useState<number>(0);
  const [cropPointsPristine, setCropPointsPristine] = useState<boolean>(true);
  const [dragDistance, setDragDistance] = useState<CropPointsCoordinates | null>(null);

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const { loaded: openCvLoaded, detectCorners, transform } = useOpenCvTools();

  const { ComputerVision: computerVisionActive } = useSettings(settingName) as {
    ComputerVision: boolean;
  };

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          setCropContainerSize({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        });
      }),
    [],
  );

  useEffect(() => {
    setOpenCvIsLoading?.(!openCvLoaded);
  }, [openCvLoaded, setOpenCvIsLoading]);

  useEffect(() => {
    if (!cropContainerRef.current) return;
    resizeObserver.observe(cropContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (openCvLoaded && imageElement && aspectRatio && computerVisionActive) {
      const cornerCoodrinates = detectCorners(imageElement);
      setDetectedCornersCoordinates(cornerCoodrinates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCvLoaded, imageElement, aspectRatio, computerVisionActive]);

  useEffect(() => {
    if (detectedCornersCoordinates && cropPointsPristine) setRelativeCropPointCoordinates(detectedCornersCoordinates);
  }, [detectedCornersCoordinates, cropPointsPristine]);

  const cropPointsCoordinates: CropPointsCoordinates = useMemo<CropPointsCoordinates>(() => {
    return points.reduce<CropPointsCoordinates>((acc, item: CropPointId) => {
      const additionalY = dragDistance?.[item]?.y || 0;
      const additionalX = dragDistance?.[item]?.x || 0;
      acc[item] = {
        x: cropContainerSize.width * (relativeCropPointsCoordinates[item].x + additionalX),
        y: cropContainerSize.height * (relativeCropPointsCoordinates[item].y + additionalY),
      };
      return acc;
    }, {} as CropPointsCoordinates);
  }, [cropContainerSize, relativeCropPointsCoordinates, dragDistance]);

  useImperativeHandle(cropperRef, () => ({
    done: async () => {
      return new Promise((resolve, reject) => {
        if (!imageElement) {
          reject('There is no source image.');
          return;
        }
        const naturalPoints = transformCornerPoints(
          relativeCropPointsCoordinates,
          imageElement,
          'naturalWidth',
          'naturalHeight',
        );
        const canvas = document.createElement('canvas');
        canvas.width = imageElement.naturalWidth;
        canvas.height = imageElement.naturalHeight;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(imageElement, 0, 0, imageElement.naturalWidth, imageElement.naturalHeight);
        const dataURL = transform(canvas, naturalPoints);
        if (!dataURL) reject('There is no image.');
        resolve({
          img: dataURL,
          cropPoints: naturalPoints,
        });
      });
    },
  }));

  const onDrag = useCallback(
    (position: Coordinates, pointId: CropPointId) => {
      const { x, y } = position;
      const newDragDistance = {
        [pointId]: { x: x / cropContainerSize.width, y: y / cropContainerSize.height },
      } as CropPointsCoordinates;

      const tempRelativeCoordinates = {
        ...relativeCropPointsCoordinates,
        [pointId]: {
          x: relativeCropPointsCoordinates[pointId].x + newDragDistance[pointId].x,
          y: relativeCropPointsCoordinates[pointId].y + newDragDistance[pointId].y,
        },
      };

      if (isConvexQuadrilateral(tempRelativeCoordinates)) {
        setDragDistance(newDragDistance);
        setMagnifierPoint(pointId);
        setCropPointsPristine(false);
      }
    },
    [cropContainerSize, relativeCropPointsCoordinates],
  );

  const onStop = useCallback(
    (event: DragEndEvent) => {
      const newCoordinates = {
        ...relativeCropPointsCoordinates,
        [event.active.id as CropPointId]: {
          x: relativeCropPointsCoordinates[event.active.id as CropPointId].x + event.delta.x / cropContainerSize.width,
          y: relativeCropPointsCoordinates[event.active.id as CropPointId].y + event.delta.y / cropContainerSize.height,
        },
      };

      if (isConvexQuadrilateral(newCoordinates)) {
        setRelativeCropPointCoordinates(newCoordinates);
      }

      setDragDistance(null);
      setMagnifierPoint(null);
    },
    [cropContainerSize, relativeCropPointsCoordinates],
  );

  return (
    <div
      className="rd-image-cropper"
      style={{
        position: 'relative',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        aspectRatio: `${aspectRatio}`,
      }}
      ref={cropContainerRef}
    >
      <DndContext sensors={sensors} onDragEnd={onStop} modifiers={[restrictToParentElement]}>
        {points.map((pointId) => (
          <CropPoint
            key={pointId}
            x={cropPointsCoordinates[pointId].x}
            y={cropPointsCoordinates[pointId].y}
            pointId={pointId}
            onDrag={onDrag}
          />
        ))}

        <CropEdges previewSize={cropContainerSize} cropPoints={cropPointsCoordinates} />

        {imageElement && (
          <CropMagnifier
            image={imageElement}
            containerSize={cropContainerSize}
            coordinates={magnifierPoint ? cropPointsCoordinates[magnifierPoint] : null}
          />
        )}

        <CrossOriginImg
          src={image}
          onLoad={(event) => {
            const imgElement = event.target as HTMLImageElement;
            setImageElement(imgElement as HTMLImageElement);
            setAspectRatio(imgElement.width / imgElement.height);
          }}
        />
      </DndContext>
    </div>
  );
};
